import React, { ReactNode } from 'react';
import {
  CustomPageLoader, FormLayoutProps, ProtectedContainerProps,
  useWaitUntilResource,
  TableOfContentsResourceInfo,
  getPage,
} from 'omni-package-react-core';
import { ErrorLayoutProps } from '~/layouts/ErrorLayout/ErrorLayout';
import { ApplicationLayoutProps } from '~/layouts/ApplicationLayout/ApplicationLayout';
import { getExternalBaseUrl } from '~/utils/companyUtils';
import { Company } from '~/interfaces/CompanyConfig';
import Constants from './Constants';

const HomePage = () => <CustomPageLoader loader={() => import('../pages/HomePage')} />;
const ProfilePage = () => <CustomPageLoader loader={() => import('../pages/ProfilePage')} />;
const ContactUsPage = () => <CustomPageLoader loader={() => import('../pages/Sitecore/ContactUsPage')} />;
const FaqPage = () => <CustomPageLoader loader={() => import('../pages/Sitecore/FaqPage')} />;
const TermsOfUsePage = () => (
  <CustomPageLoader
    loader={() => import('../pages/Sitecore/TermsOfUsePage')}
    delayPageViewUntil={useWaitUntilResource({ name: `${TableOfContentsResourceInfo.name}-terms` })}
  />
);
const ProtectionInfoPage = () => (
  <CustomPageLoader
    loader={() => import('../pages/Sitecore/ProtectionInfoPage')}
    delayPageViewUntil={useWaitUntilResource({ name: `${TableOfContentsResourceInfo.name}-pers-info` })}
  />
);
const SecurityInfoFormPage = () => <CustomPageLoader loader={() => import('../pages/SecurityInformation/SecurityInfoFormPage')} />;
const SecurityInfoConfirmationPage = () => <CustomPageLoader loader={() => import('../pages/SecurityInformation/SecurityInfoConfirmationPage')} />;
const TechnicalErrorPage = () => <CustomPageLoader loader={() => import('../pages/Errors/TechnicalErrorPage')} />;
const CentralizationErrorPage = () => <CustomPageLoader loader={() => import('../pages/Errors/CentralizationErrorPage')} />;
const UnauthorizedPage = () => <CustomPageLoader loader={() => import('../pages/Errors/UnauthorizedPage')} />;
const UnavailablePage = () => <CustomPageLoader loader={() => import('../pages/Errors/UnavailablePage')} />;
const TestPage = () => <CustomPageLoader loader={() => import('../pages/TestPage')} />;
const NotFoundPage = () => <CustomPageLoader loader={() => import('../pages/Errors/NotFoundPage')} />;
const CentralizationPage = () => <CustomPageLoader loader={() => import('../pages/Centralization/CentralizationPage')} />;
const CentralizationIntroductionPage = () => <CustomPageLoader loader={() => import('../pages/Centralization/CentralizationIntroductionPage')} />;
const CentralizationConfirmationPage = () => <CustomPageLoader loader={() => import('../pages/Centralization/CentralizationConfirmationPage')} />;

const getInternalRoutes = (
  ProtectedContainer: React.FC<ProtectedContainerProps>,
  ApplicationLayout: React.FC<ApplicationLayoutProps>,
  ErrorLayout: React.FC<ErrorLayoutProps>,
  isAuthenticated: boolean,
  FormLayout: React.FC<FormLayoutProps & { otherApplication?: boolean }>,
) => {
  const t: ReactNode[] = [];
  [
    {
      exact: true,
      path: '',
      component: (<ApplicationLayout name="Home" lob="corpo"><HomePage /></ApplicationLayout>),
      protected: true,
      index: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.Profile,
      component: (<ApplicationLayout name="Profile" lob="corpo"><ProfilePage /></ApplicationLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.ContactUs,
      component: (<ApplicationLayout name="ContactUs" lob="corpo"><ContactUsPage /></ApplicationLayout>),
      protected: false,
    },
    {
      exact: true,
      path: Constants.PagesUrl.Faq,
      component: (<ApplicationLayout name="faq" lob="corpo"><FaqPage /></ApplicationLayout>),
      protected: false,
    },
    {
      exact: true,
      path: Constants.PagesUrl.GeneralTermsOfUse,
      component: (<ApplicationLayout name="general-terms-of-use" lob="corpo"><TermsOfUsePage /></ApplicationLayout>),
      protected: false,
    },
    {
      exact: true,
      path: Constants.PagesUrl.PersonalInformationsProtection,
      component: (<ApplicationLayout name="personal-informations-protection" lob="corpo"><ProtectionInfoPage /></ApplicationLayout>),
      protected: false,
    },
    {
      exact: true,
      path: Constants.PagesUrl.SecurityInfoFormPage,
      component: (<FormLayout canQuit={false} needHelp returnPage={Constants.PagesUrl.HomePage} name="update-security-information" lob="corpo"><SecurityInfoFormPage /></FormLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.SecurityInfoConfirmationPage,
      component: (<FormLayout needHelp returnPage={Constants.PagesUrl.HomePage} name="update-security-confirmation" lob="corpo"><SecurityInfoConfirmationPage /></FormLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.Error,
      component: (<ApplicationLayout name="Error" lob="corpo"><TechnicalErrorPage /></ApplicationLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.TechnicalErrorPage,
      component: (<ErrorLayout name="Error" lob="corpo"><TechnicalErrorPage /></ErrorLayout>),
      protected: false,
    },
    {
      exact: true,
      path: Constants.PagesUrl.CentralizationErrorPage,
      component: (
        <ErrorLayout
          name="CentralizationError"
          lob="corpo"
          preventLogoClick
        >
          <CentralizationErrorPage />
        </ErrorLayout>
      ),
      protected: false,
    },
    {
      exact: true,
      path: Constants.PagesUrl.Unauthorized,
      component: (<ApplicationLayout name="Unauthorized" lob="corpo"><UnauthorizedPage /></ApplicationLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.TestPage,
      component: (<ApplicationLayout name="TestPage" lob="corpo"><TestPage /></ApplicationLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.UnavailablePage,
      component: (<ErrorLayout name="Unavailable" lob="corpo"><UnavailablePage /></ErrorLayout>),
      protected: isAuthenticated,
    },
    {
      exact: true,
      path: Constants.PagesUrl.CentralizationPage,
      component: (<FormLayout canQuit={false} needHelp returnPage={Constants.PagesUrl.HomePage} name="centralization-introduction" lob="corpo"><CentralizationIntroductionPage /></FormLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.CentralizationFormPage,
      component: (<FormLayout canQuit={false} needHelp returnPage={Constants.PagesUrl.HomePage} name="centralization" lob="corpo"><CentralizationPage /></FormLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.CentralizationConfirmationPage,
      component: (<FormLayout needHelp returnPage={Constants.PagesUrl.HomePage} name="centralization-confirmation" lob="corpo"><CentralizationConfirmationPage /></FormLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.CentralizationPageIaah,
      component: (<FormLayout canQuit={false} otherApplication needHelp returnPage={Constants.PagesUrl.HomePage} name="centralization-introduction-iaah" lob="corpo"><CentralizationIntroductionPage /></FormLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.CentralizationFormPageIaah,
      component: (<FormLayout canQuit={false} otherApplication needHelp returnPage={Constants.PagesUrl.HomePage} name="centralization-iaah" lob="corpo"><CentralizationPage /></FormLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.CentralizationConfirmationPageIaah,
      component: (<FormLayout needHelp otherApplication returnPage={getExternalBaseUrl(Company.IAAH)} name="centralization-confirmation-iaah" lob="corpo"><CentralizationConfirmationPage /></FormLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.CentralizationPageIAGP,
      component: (<FormLayout canQuit={false} otherApplication needHelp returnPage={Constants.PagesUrl.HomePage} name="centralization-introduction-iagp" lob="corpo"><CentralizationIntroductionPage /></FormLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.CentralizationFormPageIAGP,
      component: (<FormLayout canQuit={false} otherApplication needHelp returnPage={Constants.PagesUrl.HomePage} name="centralization-iagp" lob="corpo"><CentralizationPage /></FormLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.CentralizationConfirmationPageIAGP,
      component: (<FormLayout needHelp otherApplication returnPage={getExternalBaseUrl(Company.IAGP)} name="centralization-confirmation-iagp" lob="corpo"><CentralizationConfirmationPage /></FormLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.CentralizationPageIAPW,
      component: (<FormLayout canQuit={false} otherApplication needHelp returnPage={Constants.PagesUrl.HomePage} name="centralization-introduction-iapw" lob="corpo"><CentralizationIntroductionPage /></FormLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.CentralizationFormPageIAPW,
      component: (<FormLayout canQuit={false} otherApplication needHelp returnPage={Constants.PagesUrl.HomePage} name="centralization-iapw" lob="corpo"><CentralizationPage /></FormLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.CentralizationConfirmationPageIAPW,
      component: (<FormLayout needHelp otherApplication returnPage={getExternalBaseUrl(Company.IAPW)} name="centralization-confirmation-iapw" lob="corpo"><CentralizationConfirmationPage /></FormLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.CentralizationPageInvestia,
      component: (<FormLayout canQuit={false} otherApplication needHelp returnPage={Constants.PagesUrl.HomePage} name="centralization-introduction-investia" lob="corpo"><CentralizationIntroductionPage /></FormLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.CentralizationFormPageInvestia,
      component: (<FormLayout canQuit={false} otherApplication needHelp returnPage={Constants.PagesUrl.HomePage} name="centralization-investia" lob="corpo"><CentralizationPage /></FormLayout>),
      protected: true,
    },
    {
      exact: true,
      path: Constants.PagesUrl.CentralizationConfirmationPageInvestia,
      component: (<FormLayout needHelp otherApplication returnPage={getExternalBaseUrl(Company.INVESTIA)} name="centralization-confirmation-investia" lob="corpo"><CentralizationConfirmationPage /></FormLayout>),
      protected: true,
    },
    {
      exact: true,
      path: '*',
      component: (<ApplicationLayout lob="corpo" name="NotFound"><NotFoundPage /></ApplicationLayout>),
      protected: false,
    },
  ].forEach((p) => t.push(getPage(p, isAuthenticated, ProtectedContainer)));

  return t;
};

export default getInternalRoutes;
